import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Alert } from 'react-bootstrap';
import {
  useLookupEmailHelpByLastNameMutation,
  useRequestEmailHelpResetCodeMutation,
  useVerifyAndResetEmailHelpPasswordMutation,
} from '../app/api';

const brand = {
  deepBlue:    '#0a2947',
  tealBlue:    '#0069a6',
  goldenrod:   '#d09b2c',
  charcoal:    '#58585b',
  softGray:    '#e9e6e1',
  tertiaryGray:'#9d9fa1',
  green:       '#1a7f4e',
};

// Domains whose users get a brand-new prosperitycapitaladvisors.com mailbox.
// All other prior-email domains get an alias added to their existing mailbox.
const NEW_MAILBOX_DOMAINS = new Set([
  'oneteamfinancial.com',
  'hammerfinancialgroup.com',
  'fscwealthadvisors.com',
]);

function domainOf(email) {
  if (!email || typeof email !== 'string') return '';
  const at = email.lastIndexOf('@');
  return at >= 0 ? email.slice(at + 1).trim().toLowerCase() : '';
}

function migrationPathFor(priorEmail) {
  const domain = domainOf(priorEmail);
  if (NEW_MAILBOX_DOMAINS.has(domain)) return 'new_mailbox';
  return 'alias';
}

export default function EmailHelp() {
  const navigate = useNavigate();
  const [lookup, { isLoading }] = useLookupEmailHelpByLastNameMutation();
  const [requestResetCode, { isLoading: isRequestingCode }] = useRequestEmailHelpResetCodeMutation();
  const [verifyAndReset, { isLoading: isVerifying }] = useVerifyAndResetEmailHelpPasswordMutation();

  // Wizard step: 'lastname' -> 'pick' -> 'confirm' -> 'result'
  //              -> 'logged_in_q' -> 'send_offer' -> 'enter_code' -> 'done'
  // The 'logged_in_q' branch is only entered when the migration path is
  // 'new_mailbox' and the user clicks the "Need to access this?" prompt.
  const [step, setStep] = useState('lastname');
  const [stepHistory, setStepHistory] = useState([]);
  const [lastName, setLastName] = useState('');
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  // Password-reset sub-flow state.
  const [maskedPriorEmail, setMaskedPriorEmail] = useState('');
  const [codeExpiresInMinutes, setCodeExpiresInMinutes] = useState(0);
  const [code, setCode] = useState('');

  // Push the current step onto history and navigate forward. All forward
  // transitions should use this so the top-left back button works correctly.
  const goToStep = (newStep) => {
    setStepHistory((h) => [...h, step]);
    setStep(newStep);
    setError(null);
  };

  // Pop the last step off history. If history is empty we bail to Sign In.
  const goBack = () => {
    setError(null);
    if (stepHistory.length === 0) {
      navigate('/login');
      return;
    }
    const previous = stepHistory[stepHistory.length - 1];
    setStepHistory((h) => h.slice(0, -1));
    setStep(previous);
  };

  const onSubmitLastName = async (e) => {
    e.preventDefault();
    setError(null);
    const trimmed = lastName.trim();
    if (!trimmed) {
      setError('Please enter your last name.');
      return;
    }
    try {
      const result = await lookup({ last_name: trimmed }).unwrap();
      if (!result || result.length === 0) {
        setError(`We couldn't find anyone with the last name "${trimmed}" in the directory. Please double-check the spelling or contact IT.`);
        return;
      }
      setMatches(result);
      if (result.length === 1) setSelected(result[0]);
      goToStep(result.length === 1 ? 'confirm' : 'pick');
    } catch (err) {
      console.error(err);
      setError('Something went wrong looking up your name. Please try again or contact IT.');
    }
  };

  const onPick = (match) => {
    setSelected(match);
    goToStep('confirm');
  };

  const onConfirmPrior = (confirmed) => {
    if (confirmed) {
      goToStep('result');
    } else {
      // "No, that's not me" — go back to whichever previous step led here
      // (the picker if there were multiple matches; lastname otherwise).
      setSelected(null);
      if (matches.length <= 1) {
        setMatches([]);
        setLastName('');
      }
      goBack();
    }
  };

  const restart = () => {
    setStep('lastname');
    setStepHistory([]);
    setLastName('');
    setMatches([]);
    setSelected(null);
    setError(null);
    setMaskedPriorEmail('');
    setCodeExpiresInMinutes(0);
    setCode('');
  };

  const startResetFlow = () => {
    setError(null);
    setCode('');
    goToStep('logged_in_q');
  };

  const onAnsweredNeedsReset = (needsReset) => {
    if (needsReset) {
      // They want a password reset — offer to send a verification code.
      goToStep('send_offer');
    } else {
      // They can already log in — nothing more we need to do here.
      goToStep('done_no_action');
    }
  };

  const onRequestCode = async () => {
    setError(null);
    try {
      const result = await requestResetCode({ directory_id: selected.id }).unwrap();
      setMaskedPriorEmail(result.masked_prior_email || '');
      setCodeExpiresInMinutes(result.expires_in_minutes || 0);
      goToStep('enter_code');
    } catch (err) {
      setError(err?.data?.detail || 'We could not send the verification code. Please try again or contact IT.');
    }
  };

  const onSubmitCode = async (e) => {
    e.preventDefault();
    setError(null);
    const cleaned = (code || '').trim();
    if (!/^\d{6}$/.test(cleaned)) {
      setError('Please enter the 6-digit code from your email.');
      return;
    }
    try {
      const result = await verifyAndReset({ directory_id: selected.id, code: cleaned }).unwrap();
      setMaskedPriorEmail(result.masked_prior_email || maskedPriorEmail);
      goToStep('done_reset');
    } catch (err) {
      setError(err?.data?.detail || 'That code did not work. Please try again.');
    }
  };

  // ── Styles (mirror Login page aesthetics) ─────────────────────────────────
  const pageStyle = {
    minHeight: '100vh',
    backgroundImage: `url(/homepage-hero-overlay.jpg)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: brand.deepBlue,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
    position: 'relative',
  };

  const topBannerStyle = {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    backgroundColor: brand.goldenrod,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 20px',
    color: brand.deepBlue,
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  };

  const overlayStyle = {
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(to right,
      rgba(10,41,71,0.95) 0%,
      rgba(10,41,71,0.80) 38%,
      rgba(10,41,71,0.35) 58%,
      rgba(10,41,71,0.35) 72%,
      rgba(10,41,71,0.80) 88%,
      rgba(10,41,71,0.95) 100%
    )`,
  };

  const leftPaneStyle = {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  };

  const cardStyle = {
    position: 'relative',
    zIndex: 1,
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 8px 40px rgba(10,41,71,0.6)',
    width: '100%',
    maxWidth: '520px',
    minHeight: '440px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  const cardBodyStyle = {
    padding: '8px 36px 32px',
    textAlign: 'left',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  };

  // Footer row that pins to the bottom of the card. For one button the row is
  // left-aligned (default); for two buttons we space them apart.
  const footerRowOne = { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 'auto', justifyContent: 'flex-end' };
  const footerRowTwo = { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 'auto', justifyContent: 'space-between' };
  // When the footer lives inside a <Form>, the Form itself must be a flex
  // column that fills the available space so marginTop:auto on the buttons
  // pushes them to the bottom of the card.
  const formFillStyle = { display: 'flex', flexDirection: 'column', flex: 1 };
  const cardHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px 0',
    minHeight: '36px',
  };
  const iconBtnStyle = {
    background: 'transparent',
    border: 'none',
    padding: '6px 10px',
    color: brand.charcoal,
    fontSize: '18px',
    lineHeight: 1,
    cursor: 'pointer',
    borderRadius: '6px',
  };
  const headerStyle = { color: brand.deepBlue, fontWeight: 800, fontSize: '20px', marginBottom: '4px' };
  const subheaderStyle = { color: brand.charcoal, fontSize: '13px', 
    marginBottom: '20px' };
  const primaryBtnStyle = {
    backgroundColor: brand.tealBlue,
    color: '#ffffff',
    fontWeight: '700',
    letterSpacing: '1px',
    padding: '10px 14px',
    fontSize: '14px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
  };
  const secondaryBtnStyle = {
    backgroundColor: 'transparent',
    color: brand.tealBlue,
    fontWeight: '700',
    letterSpacing: '0.5px',
    padding: '10px 14px',
    fontSize: '13px',
    borderRadius: '4px',
    border: `1px solid ${brand.tealBlue}`,
    cursor: 'pointer',
  };

  const stepDot = (active, done) => ({
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: done ? brand.green : active ? brand.tealBlue : brand.softGray,
    color: done || active ? 'white' : brand.charcoal,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '12px',
  });

  const stepIndex = step === 'lastname' ? 0 : step === 'pick' ? 1 : step === 'confirm' ? 1 : 2;

  return (
    <div style={pageStyle}>
      <div style={overlayStyle} />

      <div style={topBannerStyle}>
        <i className="bi bi-envelope-paper" />
        Email Address Help
      </div>

      <div style={leftPaneStyle}>
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            {stepHistory.length > 0 ? (
              <button type="button" onClick={goBack} aria-label="Back" title="Back" style={iconBtnStyle}>
                <i className="bi bi-arrow-left" />
              </button>
            ) : <span />}
            <button type="button" onClick={() => navigate('/login')} aria-label="Close" title="Close" style={iconBtnStyle}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div style={cardBodyStyle}>

            {/* Step indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={stepDot(stepIndex === 0, stepIndex > 0)} />
              <div style={{ flex: 1, height: '2px', background: stepIndex > 0 ? brand.green : brand.softGray }} />
              <div style={stepDot(stepIndex === 1, stepIndex > 1)} />
              <div style={{ flex: 1, height: '2px', background: stepIndex > 1 ? brand.green : brand.softGray }} />
              <div style={stepDot(stepIndex === 2, false)} />
            </div>

            {error && <Alert variant="danger" style={{ fontSize: '13px' }}>{error}</Alert>}

            {/* ── Step 1: ask for last name ──────────────────────────────── */}
            {step === 'lastname' && (
              <>
                <h4 style={headerStyle}>What is your last name?</h4>
                <br/>
                <p style={subheaderStyle}>
                  We&apos;ll look you up in the company directory so we can show your
                  new Prosperity Capital Advisors email address and explain how
                  to access it.
                </p>
                <Form onSubmit={onSubmitLastName} style={formFillStyle}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '13px', color: brand.charcoal, fontWeight: 600 }}>
                      Last name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="e.g. Smith"
                      autoFocus
                      disabled={isLoading}
                    />
                  </Form.Group>
                  <div style={footerRowOne}>
                    <Button type="submit" style={primaryBtnStyle} disabled={isLoading}>
                      {isLoading ? 'Looking up…' : 'Continue'}
                    </Button>
                  </div>
                </Form>
              </>
            )}

            {/* ── Step 2a: multiple matches — pick yours ──────────────────── */}
            {step === 'pick' && (
              <>
                <h4 style={headerStyle}>We found a few matches</h4>
                <p style={subheaderStyle}>
                  Select your row so we can confirm your former email address.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                  {matches.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => onPick(m)}
                      style={{
                        textAlign: 'left',
                        background: 'white',
                        border: `1px solid ${brand.softGray}`,
                        borderRadius: '8px',
                        padding: '10px 12px',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = brand.tealBlue)}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = brand.softGray)}
                    >
                      <div style={{ fontWeight: 700, color: brand.deepBlue }}>
                        {m.first_name} {m.last_name}
                      </div>
                      <div style={{ fontSize: '12px', color: brand.charcoal }}>
                        Former email: {m.prior_email || '—'}
                        {m.prior_company ? ` · ${m.prior_company}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
                <div style={footerRowOne}>
                  <Button type="button" style={secondaryBtnStyle} onClick={restart}>
                    None of these — start over
                  </Button>
                </div>
              </>
            )}

            {/* ── Step 2b: confirm prior email ────────────────────────────── */}
            {step === 'confirm' && selected && (
              <>
                <h4 style={headerStyle}>Is this your former email?</h4>
                <br/>
                <p style={subheaderStyle}>
                  Please confirm the email address you used at your previous firm.
                </p>
                <div style={{
                  border: `1px solid ${brand.softGray}`,
                  borderRadius: '8px',
                  padding: '14px 16px',
                  marginBottom: '16px',
                  background: '#fafafa',
                }}>
                  <div style={{ fontSize: '12px', color: brand.charcoal, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                    {selected.first_name} {selected.last_name}
                    {selected.prior_company ? ` · ${selected.prior_company}` : ''}
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: brand.deepBlue, wordBreak: 'break-all' }}>
                    {selected.prior_email || '(no former email on file)'}
                  </div>
                </div>
                <div style={footerRowTwo}>
                  <Button type="button" style={primaryBtnStyle} onClick={() => onConfirmPrior(true)} disabled={!selected.prior_email}>
                    Yes, that&apos;s me
                  </Button>
                  <Button type="button" style={secondaryBtnStyle} onClick={() => onConfirmPrior(false)}>
                    No, that&apos;s not right
                  </Button>
                </div>
              </>
            )}

            {/* ── Step 3: migration path ──────────────────────────────────── */}
            {step === 'result' && selected && (() => {
              const isNewMailbox = migrationPathFor(selected.prior_email) === 'new_mailbox';
              const accentBg = isNewMailbox ? 'rgba(0,105,166,0.08)' : 'rgba(26,127,78,0.08)';
              const accentBorder = isNewMailbox ? brand.tealBlue : brand.green;
              const accentLabel = isNewMailbox ? 'New Mailbox' : 'Email Alias';
              return (
                <>
                  <h4 style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '20px', marginBottom: '4px' }}>
                    Your new Prosperity email
                  </h4>
                  <p style={{ color: brand.charcoal, fontSize: '13px', marginBottom: '16px' }}>
                    Based on your former email, here&apos;s what&apos;s been set up for you.
                  </p>

                  <div style={{
                    border: `1px solid ${accentBorder}`,
                    borderRadius: '8px',
                    background: accentBg,
                    padding: '14px 16px',
                    marginBottom: '16px',
                  }}>
                    <div style={{
                      fontSize: '11px',
                      color: accentBorder,
                      textTransform: 'uppercase',
                      letterSpacing: '1.5px',
                      fontWeight: 700,
                      marginBottom: '6px',
                    }}>
                      {accentLabel}
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: brand.deepBlue, wordBreak: 'break-all' }}>
                      {selected.final_email}
                    </div>
                    <div style={{ fontSize: '12px', color: brand.charcoal, marginTop: '6px' }}>
                      Former: <span style={{ wordBreak: 'break-all' }}>{selected.prior_email}</span>
                    </div>
                  </div>

                  {isNewMailbox ? (
                    <div style={{ fontSize: '13px', color: brand.charcoal, lineHeight: 1.55 }}>
                      <p style={{ fontWeight: 700, color: brand.deepBlue, marginBottom: '6px' }}>
                        What this means
                      </p>
                      <p style={{ marginBottom: '10px' }}>
                        Your <strong>{selected.final_email}</strong> address is a brand new
                        Microsoft 365 mailbox.
                      </p>
                      <p style={{ fontWeight: 700, color: brand.deepBlue, marginBottom: '6px' }}>
                        What to do next
                      </p>
                      <ol style={{ paddingLeft: '20px', marginBottom: '12px' }}>
                        <li>Sign in to <a href="https://outlook.office.com" target="_blank" rel="noreferrer">outlook.office.com</a> with <strong>{selected.final_email}</strong>.</li>
                        <li>The first time you sign in you&apos;ll be prompted to set up MFA — please complete this.</li>
                        <li>Forward any messages still arriving at your former address to your new mailbox if needed.</li>
                      </ol>
                    </div>
                  ) : (
                    <div style={{ fontSize: '13px', color: brand.charcoal, lineHeight: 1.55 }}>
                      <p style={{ fontWeight: 700, color: brand.deepBlue, marginBottom: '6px' }}>
                        What this means
                      </p>
                      <p style={{ marginBottom: '10px' }}>
                        <strong>{selected.final_email}</strong> has been added as an
                        <em> alias</em> on your existing <strong>{selected.prior_email}</strong>{' '}
                        mailbox. You don&apos;t have a separate inbox to log into — anything sent
                        to either address lands in the same place.
                      </p>
                      <p style={{ fontWeight: 700, color: brand.deepBlue, marginBottom: '6px' }}>
                        What to do next
                      </p>
                      <ol style={{ paddingLeft: '20px', marginBottom: '12px' }}>
                        <li>Keep signing in to your current mailbox as you do today — nothing changes there.</li>
                        <li>When sending new external messages, choose your <strong>{selected.final_email}</strong> address as the &quot;From&quot; so recipients see the Prosperity brand.</li>
                      </ol>
                    </div>
                  )}

                  {isNewMailbox ? (
                    <div style={footerRowOne}>
                      <Button type="button" style={primaryBtnStyle} onClick={startResetFlow}>
                        Need access to this mailbox?
                      </Button>
                    </div>
                  ) : (
                    <div style={{ marginTop: 'auto' }}>
                      <p style={{
                        textAlign: 'center',
                        fontSize: '13px',
                        color: brand.deepBlue,
                        fontWeight: 700,
                        marginBottom: '10px',
                      }}>
                        Do you need help to add the From address?
                      </p>
                      <br/>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        <Button type="button" style={primaryBtnStyle} onClick={() => goToStep('alias_from_help')}>
                          Yes, show me how
                        </Button>
                        <Button type="button" style={secondaryBtnStyle} onClick={() => goToStep('alias_login_info')}>
                          No, I&apos;m good
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* ── New-mailbox sub-flow: do you need a password reset? ─────── */}
            {step === 'logged_in_q' && selected && (
              <>
                <h4 style={headerStyle}>
                  Do you need help accessing the mailbox?
                </h4>
                <br/>
                <p style={subheaderStyle}>
                  Would you like help to reset your password for{' '}
                  <strong style={{ wordBreak: 'break-all' }}>{selected.final_email}</strong>?
                </p>
                <div style={footerRowTwo}>
                  <Button type="button" style={primaryBtnStyle} onClick={() => onAnsweredNeedsReset(true)}>
                    Yes, reset my password
                  </Button>
                  <Button type="button" style={secondaryBtnStyle} onClick={() => onAnsweredNeedsReset(false)}>
                    No, I can log in
                  </Button>
                </div>
              </>
            )}

            {/* ── New-mailbox sub-flow: offer to send temp password ──────── */}
            {step === 'send_offer' && selected && (
              <>
                <h4 style={headerStyle}>Send a temporary password?</h4>
                <br/>
                <p style={subheaderStyle}>
                  First we need to confirm your identity.  To do that we will email a 6-digit verification code to your former
                  address. Once you enter it, we&apos;ll reset your new mailbox
                  password and email the temporary password to the same
                  address.
                </p>
                <div style={{
                  border: `1px solid ${brand.softGray}`,
                  borderRadius: '8px',
                  padding: '12px 14px',
                  marginBottom: '14px',
                  background: '#fafafa',
                  fontSize: '13px',
                  color: brand.charcoal,
                }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                    Code &amp; password will be sent to
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: brand.deepBlue, wordBreak: 'break-all' }}>
                    {selected.prior_email}
                  </div>
                </div>
                {error && <Alert variant="danger" style={{ fontSize: '13px' }}>{error}</Alert>}
                <div style={footerRowTwo}>
                  <Button type="button" style={primaryBtnStyle} onClick={onRequestCode} disabled={isRequestingCode}>
                    {isRequestingCode ? 'Sending…' : 'Yes, send the code'}
                  </Button>
                  <Button type="button" style={secondaryBtnStyle} onClick={goBack}>
                    No, go back
                  </Button>
                </div>
              </>
            )}

            {/* ── New-mailbox sub-flow: enter verification code ──────────── */}
            {step === 'enter_code' && selected && (
              <>
                <h4 style={headerStyle}>Enter your verification code</h4>
                <br/>
                <p style={subheaderStyle}>
                  We sent a 6-digit code to <strong>{maskedPriorEmail || selected.prior_email}</strong>.
                  {codeExpiresInMinutes ? ` It expires in ${codeExpiresInMinutes} minutes.` : ''}
                </p>
                <p style={subheaderStyle}>
                This email may take a few moments to arrive depending on traffic.</p>
                <Form onSubmit={onSubmitCode} style={formFillStyle}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '13px', color: brand.charcoal, fontWeight: 600 }}>
                      Verification code
                    </Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      autoFocus
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="------"
                      disabled={isVerifying}
                      style={{ letterSpacing: '6px', fontSize: '18px', fontWeight: 700 }}
                    />
                  </Form.Group>
                  <div style={footerRowTwo}>
                    <Button type="submit" style={primaryBtnStyle} disabled={isVerifying || code.length !== 6}>
                      {isVerifying ? 'Verifying…' : 'Verify & reset password'}
                    </Button>
                    <Button type="button" style={secondaryBtnStyle} onClick={goBack} disabled={isVerifying}>
                      Back
                    </Button>
                  </div>
                </Form>
              </>
            )}

            {/* ── Sub-flow terminal states ───────────────────────────────── */}
            {step === 'done_reset' && selected && (
              <>
                <h4 style={{ ...headerStyle, color: brand.green }}>
                  <i className="bi bi-check-circle me-2" />
                  Temporary password sent
                </h4>
                <br/>
                <p style={subheaderStyle}>
                  Your password has been reset and the temporary password was
                  emailed to <strong>{maskedPriorEmail || selected.prior_email}</strong>.
                </p>
                <p style={subheaderStyle}>
                  Sign in on a web browser at <a href="https://outlook.office.com" target="_blank" rel="noreferrer">outlook.office.com</a>
                  &nbsp;with <strong>{selected.final_email}</strong>; you&apos;ll be
                  prompted to set a permanent password.  Once you are set up, you can either:
                </p>
                  <p style={subheaderStyle}>&nbsp;&nbsp;&nbsp;* Close this pop up and log into this website with your new prosperity email </p>
                  <p style={subheaderStyle}>&nbsp;&nbsp;&nbsp;* Add your new email to your desktop outlook</p>
                  <p style={subheaderStyle}>&nbsp;&nbsp;&nbsp;* Do Both!</p>
                  <br/>
                <div style={{
                  padding: '12px 14px',
                  background: 'rgba(0,105,166,0.06)',
                  border: `1px solid ${brand.tealBlue}`,
                  borderRadius: '8px',
                  marginTop: 'auto',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '13px', color: brand.deepBlue, fontWeight: 700, marginBottom: '8px' }}>
                    Need help adding this mailbox to Outlook?
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button type="button" style={primaryBtnStyle} onClick={() => goToStep('outlook_tutorial')}>
                      Yes, show me how
                    </Button>
                  </div>
                </div>
              </>
            )}

            {step === 'done_no_action' && selected && (
              <>
                <br/>
                <h4 style={headerStyle}>You&apos;re all set</h4>
                <br/>
                <br/>
                <p style={subheaderStyle}>
                  Since you&apos;ve already signed in to <strong>{selected.final_email}</strong>,
                  there&apos;s nothing more to do here.
                </p>
                <div style={{
                  padding: '12px 14px',
                  background: 'rgba(0,105,166,0.06)',
                  border: `1px solid ${brand.tealBlue}`,
                  borderRadius: '8px',
                  marginTop: 'auto',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '13px', color: brand.deepBlue, fontWeight: 700, marginBottom: '8px' }}>
                    Need help adding this mailbox to Outlook?
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button type="button" style={primaryBtnStyle} onClick={() => goToStep('outlook_tutorial')}>
                      Yes, show me how
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* ── Outlook tutorial step ──────────────────────────────────── */}
            {step === 'outlook_tutorial' && selected && (
              <>
                <h4 style={headerStyle}>Adding your mailbox to Outlook</h4>
                <p style={subheaderStyle}>
                  Follow these steps to add <strong>{selected.final_email}</strong> to Outlook.
                </p>
                <div style={{ fontSize: '13px', color: brand.charcoal, lineHeight: 1.55 }}>
                  <p style={{ fontWeight: 700, color: brand.deepBlue, marginBottom: '6px' }}>
                    Classic Outlook for Windows
                  </p>
                  <ol style={{ paddingLeft: '20px', marginBottom: '14px' }}>
                    <li>Open Outlook.</li>
                    <li>Click <strong>File</strong> in the top-left corner.</li>
                    <li>Click <strong>Add Account</strong>.</li>
                    <li>Enter <strong>{selected.final_email}</strong> and click <strong>Connect</strong>.</li>
                    <li>Enter your password when prompted and complete any MFA setup.</li>
                    <li>Click <strong>Done</strong>. Your new mailbox will appear in the folder list on the left.</li>
                    <li>Restart Outlook if the mailbox doesn&apos;t show up right away.</li>
                  </ol>
                  <p style={{ fontWeight: 700, color: brand.deepBlue, marginBottom: '6px' }}>
                    New Outlook for Windows
                  </p>
                  <ol style={{ paddingLeft: '20px', marginBottom: '14px' }}>
                    <li>Open the New Outlook app.</li>
                    <li>Go to <strong>File → Settings → Accounts → Add account</strong>.</li>
                    <li>Enter <strong>{selected.final_email}</strong> and click <strong>Continue</strong>.</li>
                    <li>Enter your password when prompted and complete any MFA setup.</li>
                    <li>Click <strong>Done</strong>. Your new mailbox will appear in the folder list on the left.</li>
                    <li>Restart New Outlook if the mailbox doesn&apos;t show up right away.</li>
                  </ol>
                  <p style={{ fontWeight: 700, color: brand.deepBlue, marginBottom: '6px' }}>
                    Outlook for Mac
                  </p>
                  <ol style={{ paddingLeft: '20px', marginBottom: '14px' }}>
                    <li>Open Outlook.</li>
                    <li>From the menu bar, choose <strong>Outlook → Settings → Accounts</strong>.</li>
                    <li>Click the <strong>+</strong> in the bottom-left and choose <strong>New Account</strong>.</li>
                    <li>Enter <strong>{selected.final_email}</strong> and follow the prompts.</li>
                  </ol>
                  <p style={{ fontWeight: 700, color: brand.deepBlue, marginBottom: '6px' }}>
                    Outlook on the web
                  </p>
                  <p style={{ marginBottom: '14px' }}>
                    No setup required — just sign in at{' '}
                    <a href="https://outlook.office.com" target="_blank" rel="noreferrer">outlook.office.com</a>
                    {' '}with <strong>{selected.final_email}</strong>.
                  </p>
                </div>
                <div style={footerRowOne}>
                  <Button type="button" style={primaryBtnStyle} onClick={() => goToStep('all_set')}>
                    Done
                  </Button>
                </div>
              </>
            )}

            {/* ── Final "all set" message ────────────────────────────────── */}
            {step === 'all_set' && selected && (
              <>
                <br/>
                <h4 style={{ ...headerStyle, color: brand.green }}>
                  <i className="bi bi-check-circle me-2" />
                  You&apos;re all set!
                </h4>
                <br/>
                <br/>
                <p style={subheaderStyle}>
                  You can now close this pop-up and log into this website using your newly set up{' '}
                  <strong style={{ wordBreak: 'break-all' }}>{selected.final_email}</strong>.
                </p>
              </>
            )}

            {/* ── Alias sub-flow: how to add the new From address ────────── */}
            {step === 'alias_from_help' && selected && (
              <>
                <h4 style={headerStyle}>Adding your new From address</h4>
                <br/>
                <p style={subheaderStyle}>
                  Follow these steps in <strong>Outlook on the web</strong> (browser only):
                </p>
                <ol style={{ paddingLeft: '20px', fontSize: '13px', color: brand.charcoal, lineHeight: 1.55 }}>
                  <li>Sign in at <a href="https://outlook.office.com" target="_blank" rel="noreferrer">outlook.office.com</a>.</li>
                  <li>Go to <strong>File → Settings → Mail → Compose and Reply</strong>.</li>
                  <li>Under the addresses listed, make sure <strong>{selected.final_email}</strong> is checked.</li>
                </ol>
                <div style={footerRowOne}>
                  <Button type="button" style={primaryBtnStyle} onClick={() => goToStep('alias_login_info')}>
                    Done
                  </Button>
                </div>
              </>
            )}

            {/* ── Alias sub-flow: which email to log in with ─────────────── */}
            {step === 'alias_login_info' && selected && (
              <>
                <h4 style={headerStyle}>Which email do I log in with?</h4>
                <br/>
                <p style={subheaderStyle}>
                  You can log into this website using your{' '}
                  <strong style={{ wordBreak: 'break-all' }}>{selected.prior_email}</strong> address.
                </p>
                <p style={subheaderStyle}>
                  Once you make your{' '}
                  <strong style={{ wordBreak: 'break-all' }}>{selected.final_email}</strong> address
                  your primary email, you&apos;ll use that to log in going forward.
                </p>
                <p style={subheaderStyle}>
                  Now close this pop up and log in!
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

